import { Component, Injector, Input } from '@angular/core';
import { IContactListColumnSchems, generateFormGroupForAttendanceGridRow, prepareColmnSchema } from './contact-list.schema';
import { AttendanceContactDetail, AttendanceContactInfo, AttendanceGridRow } from 'attendance/model/attendance.model';
import { FormBuilder, FormGroup } from '@angular/forms';
import { PersistantStorageService, StorageType } from 'shared/persistant-storage.service';
import { Subject } from 'rxjs';
import { confirmLoadUnsavedChanges } from 'projects/evide-client/src/app/app.const';
import { isAnyFieldLoading } from 'shared/shared.helper';
import { FamilyService } from '@app/services/family/family.service';
import { IGetFamilyHeaderByProjectId, familyDisplayLabel } from '@app/models/family/family-grid.model';
import { AuthenticationService } from '@core/services/auth.service';

export type AttendanceContactPageData = {
  gridRowLists: AttendanceGridRow[],
  formGroup: FormGroup,
  formGuid: string,
  activitySessionGuid: string,
  isUnsaved?: boolean,
  formSaveSuccess$?: Subject<boolean>
};

@Component({
    selector: 'attendance-contact-list',
    templateUrl: './contact-list.component.html',
    providers: [],
    standalone: false
})
export class ContactListComponent {

  @Input() pageData: AttendanceContactPageData;

  @Input() set contactDetail(detail: AttendanceContactDetail | undefined) {
    this.onContactDetail(detail);
  }
  @Input() autoSave = true;

  // The EXTERNAL attendance sheet (evide-client) compiles this same component but has no
  // user session — the Family endpoints would just 401. Hosts without family support turn
  // the filter off explicitly so no anonymous request is ever fired.
  @Input() showFamilyFilter = true;

  private initialValues;
  private storageKey = '';
  private autoSaveInitialised = false;
  public displayedColumns: string[] = [];
  columnsSchema: Readonly<IContactListColumnSchems[]> = [];

  // Group attendance sheets can hold dozens of attendees — the search narrows the rows so
  // one person's times can be found and edited. Matching rows are HIDDEN, never removed:
  // every row's form controls must stay registered or a save while filtered would silently
  // drop the hidden attendees' answers.
  public searchTerm = '';

  // Family filter: family services mark a whole family's attendance together. Same
  // hide-don't-remove contract as the search — the family test lives INSIDE rowMatches so
  // a save while filtered can never drop non-family attendees' answers. Only families with
  // at least one member ON THE SHEET are offered (anything else filters to an empty table);
  // the control hides itself when none qualify / no grant.
  private allFamilies: IGetFamilyHeaderByProjectId[] = [];
  public selectedFamilyGuid: string | null = null;
  private familyMemberGuids: Set<string> | null = null;

  // CACHED, not a getter: a getter hands ng-select a NEW array identity every change
  // detection pass, so it rebuilds its option list mid-click and the clicked option is
  // destroyed before the click completes — selection never registers (same pathology as
  // the builder-form-select trackBy note). Recomputed only when families or rows change.
  public familyOptions: IGetFamilyHeaderByProjectId[] = [];

  private recomputeFamilyOptions() {
    const rows = this.pageData?.gridRowLists ?? [];
    // Guid comparisons are lowercase-normalised throughout — .NET serialises guids
    // lowercase but stored/legacy values have varied casing before.
    const rowGuids = new Set(rows.map((r) => (r.contactGuid ?? '').toLowerCase()));
    this.familyOptions = this.allFamilies.filter(
      (f) =>
        // Field ABSENT (older API): can't pre-check membership, offer it.
        // Field EMPTY: the family genuinely has no members — never offer it.
        f.familyContacts === undefined ||
        f.familyContacts.some((g) => rowGuids.has((g ?? '').toLowerCase()))
    );
  }

  public rowMatches(gridRow: AttendanceGridRow): boolean {
    if (
      this.familyMemberGuids &&
      !this.familyMemberGuids.has((gridRow.contactGuid ?? '').toLowerCase())
    ) {
      return false;
    }
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) { return true; }
    return gridRow.attendanceContacts.some(c =>
      (c.dataStringGridValue ?? '').toLowerCase().includes(term));
  }

  public get anyRowMatches(): boolean {
    return this.pageData?.gridRowLists?.some(r => this.rowMatches(r)) ?? true;
  }

  public get hasActiveFilter(): boolean {
    return !!this.searchTerm || !!this.selectedFamilyGuid;
  }

  public onFamilyChange(familyGuid: string | null) {
    this.selectedFamilyGuid = familyGuid;
    if (!familyGuid) {
      this.familyMemberGuids = null;
      return;
    }
    const header = this.allFamilies.find((f) => f.familyGuid === familyGuid);
    if (header?.familyContacts?.length) {
      // Membership rode the header response — narrow instantly, no second request.
      this.familyMemberGuids = new Set(
        header.familyContacts.map((g) => (g ?? '').toLowerCase())
      );
      return;
    }
    // Older API without member guids on the header: fall back to the member fetch.
    this.familyService.getFamilyContactsByFamilyGuid(familyGuid).subscribe({
      next: (members) => {
        this.familyMemberGuids = new Set(
          (members ?? [])
            .map((m: any) => m?.contact?.contactGuid ?? m?.contactGuid)
            .filter(Boolean)
            .map((g: string) => g.toLowerCase())
        );
      },
      // A failed member load must not leave a stale filter hiding rows — clear it,
      // and leave a breadcrumb (a silent clear looks like "the filter does nothing").
      error: (err) => {
        console.error('Family member lookup failed — family filter cleared', err);
        this.selectedFamilyGuid = null;
        this.familyMemberGuids = null;
      }
    });
  }

  private loadFamilies() {
    this.familyService.getFamilysHeaderByProjectId().subscribe({
      next: (families) => {
        this.allFamilies = (families ?? []).map((f) => ({
          ...f,
          displayLabel: familyDisplayLabel(f)
        }));
        this.recomputeFamilyOptions();
      },
      error: () => {
        this.allFamilies = [];
        this.recomputeFamilyOptions();
      }
    });
  }

  constructor(
    private fb: FormBuilder, private storage: PersistantStorageService,
    private familyService: FamilyService,
    private injector: Injector
  ) { }

  // The Families MODULE toggle (project settings) — same gate as the sidebar menu and
  // the customer-profile Family tab: module off means no family features anywhere,
  // including this filter, even when old family data exists. Resolved LAZILY via the
  // injector: this shared component is also compiled into the external evide-client
  // app (showFamilyFilter=false there), which must never construct the admin
  // AuthenticationService — a constructor injection would.
  private get familyModuleEnabled(): boolean {
    try {
      const auth = this.injector.get(AuthenticationService);
      return auth?.currentUser?.familyEnabled !== false;
    } catch {
      return false;
    }
  }

  private onContactDetail(detail: AttendanceContactDetail | undefined) {
    if (!detail) { return }
    this.pageData.formGuid = detail.formGuid;
    this.pageData.activitySessionGuid = detail.activitySessionGuid;
    this.storageKey = `attendance-${detail.activitySessionGuid}-${this.pageData.formGuid}`;

    this.columnsSchema = [...prepareColmnSchema(detail.attendanceContactInfos)]
    this.generatePageForms(detail.gridRows, detail.attendanceContactInfos);
    this.displayedColumns = this.columnsSchema.map((col) => col.key);
    this.assignValue();
    // Family filter is only worth offering where the search is (group sheets), and only
    // on hosts that support it. Deferred a microtask for the same input-order reason as
    // the auto-save setup below: the contactDetail setter can fire before
    // [showFamilyFilter] is bound.
    Promise.resolve().then(() => {
      if (this.showFamilyFilter && this.familyModuleEnabled
          && (detail.gridRows?.length ?? 0) > 1 && !this.allFamilies.length) {
        this.loadFamilies();
      } else {
        this.recomputeFamilyOptions(); // rows changed under an already-loaded family list
      }
    });
    // Defer the auto-save/draft-restore setup to a microtask so it reads the FINAL @Input values
    // and runs only once. When this component is created via *ngIf with contactDetail already
    // populated, Angular sets inputs in template order — the contactDetail setter can fire before
    // [autoSave] is bound, so reading autoSave synchronously here saw the default (true) and showed
    // the "unsaved changes" prompt even on screens that pass [autoSave]="false".
    Promise.resolve().then(() => {
      if (!this.autoSave || this.autoSaveInitialised) { return }
      this.autoSaveInitialised = true;
      this.enableAutoSave();
      this.checkApplySavedChanges();
      this.registerSaveSuccess();
    });
  }

  private generatePageForms(
    gridRows: ReadonlyArray<AttendanceGridRow>,
    attendanceContactInfos: Array<AttendanceContactInfo>
  ) {
    let currentForm = this.pageData.formGroup ?? this.fb.group({});
    this.pageData.gridRowLists = [];
    gridRows.forEach((gridRow) => {
      currentForm.addControl(gridRow.uuid, generateFormGroupForAttendanceGridRow(this.fb, gridRow, attendanceContactInfos));
      this.pageData.gridRowLists.push(gridRow);
    });
  }

  private assignValue() {
    const { formGroup: pageDataFormGorup, gridRowLists } = this.pageData;

    this.columnsSchema.forEach(({ detailInfo, formGroup }) => {
      formGroup.controls[detailInfo.evideInputContentGuid].valueChanges.subscribe(val => {
        gridRowLists.forEach(({ uuid: uuid }) => {
          const rowFormGroup: FormGroup = pageDataFormGorup.controls[uuid] as FormGroup;
          rowFormGroup.controls[detailInfo.evideInputContentGuid].setValue(val);
        })
      })
    });
  }

  private enableAutoSave() {
    this.initialValues = JSON.stringify(this.pageData.formGroup.getRawValue());
    for (const groupUuid in this.pageData.formGroup.controls) {
      const formGroup: FormGroup = this.pageData.formGroup.controls[groupUuid] as FormGroup;
      for (const fieldUuid in formGroup.controls) {
        formGroup.controls[fieldUuid].valueChanges.subscribe(val => {
          if (!isAnyFieldLoading(this.pageData.gridRowLists)) {
            this.onFieldChange(groupUuid, fieldUuid, val);
          }
        })
      }
    }
  }

  private onFieldChange(groupUuid, fieldUuid, val) {
    this.pageData.isUnsaved = this.initialValues !== JSON.stringify(this.pageData.formGroup.getRawValue());
    if (this.pageData.isUnsaved) {
      let storedData = this.storage.getData(this.storageKey, StorageType.JSON);
      if (!storedData) { storedData = {} };
      storedData[`${groupUuid}$${fieldUuid}`] = val;
      this.storage.setData(this.storageKey, storedData, StorageType.JSON);
    } else {
      this.storage.removeData(this.storageKey);
    }
  }

  private checkApplySavedChanges() {
    let storedData = this.storage.getData(this.storageKey, StorageType.JSON);
    const isApplySavedChages = !!storedData;
    if (!isApplySavedChages) {
      return;
    }
    if (confirmLoadUnsavedChanges()) {
      this.applySavedChanges();
    } else {
      this.storage.removeData(this.storageKey);
    }
  }

  public applySavedChanges() {
    let storedData = this.storage.getData(this.storageKey, StorageType.JSON);

    Object.keys(storedData).forEach(key => {
      const [groupUuid, fieldUuid] = key.split('$');
      const formGroup: FormGroup | undefined = this.pageData.formGroup.controls[groupUuid] as FormGroup;
      const value = storedData[key];
      if (formGroup) {
        const formControl = formGroup.controls[fieldUuid];
        if (formControl) {
          const currentValue = formControl.value;
          if (Object.prototype.toString.call(currentValue) === '[object Date]') {
            formControl.setValue(new Date(value))
          } else {
            formControl.setValue(value)
          }
        }
      }
    })
  }

  private registerSaveSuccess() {
    this.pageData.formSaveSuccess$?.subscribe((res) => {
      if (res) {
        this.storage.removeData(this.storageKey);
      }
    })
  }

}